import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SaveBar } from '@/components/ui/save-bar';
import { useBrief, useUpdateBriefSlice } from '@/lib/brief';

export const Route = createFileRoute('/_app/brief/identity')({
  staticData: { crumb: 'O klinice' },
  component: IdentityPage,
});

const IdentitySchema = z.object({
  founded: z.string().regex(/^\d{4}$/u, 'Rok w formacie YYYY').optional().or(z.literal('')),
  founders: z.string().max(2000).optional().or(z.literal('')),
  mission: z.string().min(40, 'Min. 40 znaków').max(800).optional().or(z.literal('')),
  manifesto: z.string().min(200, 'Min. 200 znaków').max(4000).optional().or(z.literal('')),
  pull_quote: z.string().min(60).max(300).optional().or(z.literal('')),
});
type IdentityInput = z.infer<typeof IdentitySchema>;

function IdentityPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');

  const defaults: IdentityInput = React.useMemo(
    () => ({
      founded: brief?.identity?.founded ?? '',
      founders: (brief?.identity?.founders ?? []).join('\n'),
      mission: brief?.identity?.mission ?? '',
      manifesto: brief?.identity?.manifesto ?? '',
      pull_quote: brief?.identity?.pull_quote ?? '',
    }),
    [brief]
  );

  const form = useForm<IdentityInput>({
    resolver: zodResolver(IdentitySchema),
    defaultValues: defaults,
    values: defaults,
  });

  const onSubmit = async (data: IdentityInput) => {
    setSavedStatus('idle');
    try {
      const identity = {
        ...(brief?.identity ?? {}),
        ...(data.founded ? { founded: data.founded } : {}),
        founders: data.founders
          ? data.founders.split('\n').map((s) => s.trim()).filter(Boolean)
          : [],
        ...(data.mission ? { mission: data.mission } : {}),
        ...(data.manifesto ? { manifesto: data.manifesto } : {}),
        ...(data.pull_quote ? { pull_quote: data.pull_quote } : {}),
      };
      await update.mutateAsync({ slice: 'identity', data: identity });
      setSavedStatus('saved');
      form.reset(data);
    } catch {
      setSavedStatus('error');
    }
  };

  const manifestoLen = (form.watch('manifesto') ?? '').length;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8" noValidate>
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">O klinice</h1>
        <p className="mt-1 text-sm text-text-muted">
          Tożsamość kliniki: rok założenia, założyciele, misja, manifest. Te dane
          napędzają sekcję „O nas" oraz początkowe akapity wygenerowanych stron.
        </p>
      </header>

      <section className="space-y-5 rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
        <div className="grid gap-5 md:grid-cols-2">
          <Field
            label="Rok założenia"
            htmlFor="founded"
            hint="opcjonalnie"
            error={form.formState.errors.founded?.message}
          >
            <Input id="founded" placeholder="2014" {...form.register('founded')} />
          </Field>
        </div>
        <Field
          label="Założyciele"
          htmlFor="founders"
          hint="jeden założyciel w linii"
          error={form.formState.errors.founders?.message}
        >
          <Textarea
            id="founders"
            rows={3}
            placeholder={`lek. wet. Anna Kowalska\ndr n. wet. Marek Nowak`}
            {...form.register('founders')}
          />
        </Field>
      </section>

      <section className="space-y-5 rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
        <Field
          label="Misja"
          htmlFor="mission"
          hint="40-800 znaków, 1-3 zdania"
          error={form.formState.errors.mission?.message}
        >
          <Textarea id="mission" rows={3} {...form.register('mission')} />
        </Field>

        <Field
          label="Manifest"
          htmlFor="manifesto"
          hint={`${manifestoLen}/4000 znaków, min. 200`}
          error={form.formState.errors.manifesto?.message}
        >
          <Textarea
            id="manifesto"
            rows={8}
            placeholder="Tu opowiedz, jak pracujecie, w czym się specjalizujecie, jakie standardy stosujecie..."
            {...form.register('manifesto')}
          />
        </Field>

        <Field
          label="Cytat-podtytuł (opcjonalnie)"
          htmlFor="pull_quote"
          hint="60-300 znaków, krótkie hasło przewodnie"
          error={form.formState.errors.pull_quote?.message}
        >
          <Textarea id="pull_quote" rows={2} {...form.register('pull_quote')} />
        </Field>
      </section>

      <SaveBar
        saving={form.formState.isSubmitting || update.isPending}
        dirty={form.formState.isDirty}
        status={savedStatus}
        onCancel={() => form.reset(defaults)}
      />
    </form>
  );
}
